import { faBadgeCheck, faHourglass } from "@fortawesome/pro-regular-svg-icons";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React, { useCallback } from "react";

import { slugToParams } from "@/mk2/apps/bazaar/helpers";
import { bazaarProductDetailUrl } from "@/mk2/apps/bazaar/urls";
import { blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { faqVerifiedReviewUrl } from "@/mk2/apps/faq/urls";
import ReviewMenu from "@/mk2/apps/strollers/components/ReviewMenu";
import { strollersDetailUrl, strollersReviewPhotoUrl } from "@/mk2/apps/strollers/urls";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { Dot } from "@/mk2/components/Dot";
import { Emojify } from "@/mk2/components/Emojify";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { PhotosRow } from "@/mk2/components/PhotosRow";
import { PositivesNegativesList } from "@/mk2/components/PositivesNegativesList";
import { StarsRating } from "@/mk2/components/StarsRating";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import {
    PhotoEntity,
    ProductEntity,
    ProductQuality,
    StrollerEntity,
    StrollerUserReviewEntity,
    StrollerUserReviewStatus,
} from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    REVIEW_OF_ALIKE_MODEL_stroller,
    REVIEW_VERIFIED,
    STROLLERS_DETAILED_REVIEW,
    STROLLERS_NOT_APPROVED,
    STROLLER_REVIEW_RELATED_PRODUCT_IN_BAZAAR,
    USEFUL,
    count_PEOPLE_THINK_IT_IS_USEFUL,
    counter_PHOTOS,
} from "@/mk/autogenerated/translation";
import { linebreaksbr } from "@/mk/bazaar/common/filters";
import { formatPrice } from "@/mk/bazaar/common/formatters";

import styles from "./Review.mscss";

interface OwnProps {
    className?: string;
    stroller: StrollerEntity; // kocik ku ktoremu zobrazujeme recenziu
    review: StrollerUserReviewEntity;
    reviewStroller: StrollerEntity; // kocik ku ktoremu bola napisana recenzia (moze byt suvisiaci model)
    product?: ProductEntity;
    isAdmin: boolean;

    onToggleMenuMore(reviewId: number);
}

type Props = OwnProps;

const MAX_WIDTH = 584;

export const Review: React.FunctionComponent<Props> = React.memo(
    ({ className, stroller, review, reviewStroller, product, isAdmin, onToggleMenuMore }) => {
        if (!reviewStroller || review.strollerId !== reviewStroller.id) {
            throw new Error("Review stroller mismatch");
        }

        const galleryLinkTo = useCallback(
            (photoFile: PhotoEntity) =>
                review.status === StrollerUserReviewStatus.NOT_VERIFIED_AND_APPROVED ||
                review.status === StrollerUserReviewStatus.VERIFIED_AND_APPROVED
                    ? url(strollersReviewPhotoUrl, {
                          slug: stroller.slug,
                          reviewId: review.id,
                          photoId: photoFile.id,
                      })
                    : // fotky neschvalenych recenzii nemaju stranku s detailom fotky
                      null,
            [stroller, review],
        );

        return (
            <div
                className={cx(
                    styles.Review,
                    className,
                    review.status !== StrollerUserReviewStatus.NOT_VERIFIED_AND_APPROVED &&
                        review.status !== StrollerUserReviewStatus.VERIFIED_AND_APPROVED &&
                        styles["Review--notApproved"],
                )}
            >
                {stroller.id !== review.strollerId && (
                    <Interpolate
                        className={styles.Review__alike}
                        parent="div"
                        i18nKey={REVIEW_OF_ALIKE_MODEL_stroller}
                        stroller={
                            <Link to={url(strollersDetailUrl, { slug: reviewStroller.slug })}>
                                {reviewStroller.name}
                            </Link>
                        }
                    />
                )}

                {review.user && (
                    <div className={styles.Review__user}>
                        <AvatarImg
                            size={24}
                            user={review.user}
                            className={styles.Review__user__avatar}
                        />
                        <Link
                            to={url(blogsProfileUrl, { username: review.user.username })}
                            className={styles.Review__user__username}
                        >
                            {review.user.username}
                        </Link>
                        <Dot spaces={1} className={styles.Review__user__dot} />
                        <span className={styles.Review__user__createdAt}>
                            {timeAt(review.createdTime)}
                        </span>
                        {review.status === StrollerUserReviewStatus.VERIFIED_AND_APPROVED && (
                            <span>
                                <Link
                                    to={faqVerifiedReviewUrl}
                                    className={styles.Review__user__verified}
                                >
                                    <FontAwesomeIcon icon={faBadgeCheck} /> {REVIEW_VERIFIED}
                                </Link>
                            </span>
                        )}
                    </div>
                )}

                {product && product.quality === ProductQuality.OLD && (
                    <div className={styles.Review__product}>
                        <span className={styles.Review__product__label}>
                            {STROLLER_REVIEW_RELATED_PRODUCT_IN_BAZAAR}
                        </span>{" "}
                        <Link to={url(bazaarProductDetailUrl, slugToParams(product.slug))}>
                            {product.title} -{" "}
                            <span className={styles.Review__product__price}>
                                {formatPrice(product.price)}
                            </span>
                        </Link>
                    </div>
                )}

                {isAdmin && (
                    <ReviewMenu
                        className={styles.Review__dropdownMenu}
                        review={review}
                        onToggleMenuMore={onToggleMenuMore}
                    />
                )}

                <div className={styles.Review__score}>
                    <StarsRating starsCount={review.score} canShowHalf />
                    {review.approved && review.detailedReview && (
                        <>
                            <Dot spaces={1} />
                            <div
                                className={cx(
                                    styles.Review__score__badge,
                                    styles["Review__score__badge--detailedReview"],
                                )}
                            >
                                <FontAwesomeIcon icon={faStar} className={styles.Review__icon} />
                                &nbsp;
                                {STROLLERS_DETAILED_REVIEW}
                            </div>
                        </>
                    )}
                    {review.approved === false && !review.deleted ? (
                        <>
                            <Dot spaces={1} />
                            <div className={styles.Review__score__badge}>
                                <FontAwesomeIcon
                                    icon={faHourglass}
                                    className={styles.Review__score__icon}
                                />
                                &nbsp;
                                {STROLLERS_NOT_APPROVED}
                            </div>
                        </>
                    ) : null}
                </div>

                <div className={styles.Review__oneliner}>
                    <Emojify>{review.oneliner}</Emojify>
                </div>

                <div className={styles.Review__text}>
                    <Emojify>{linebreaksbr(review.text)}</Emojify>
                </div>

                {((review.positives && review.positives.length > 0) ||
                    (review.negatives && review.negatives.length > 0)) && (
                    <PositivesNegativesList
                        positives={review.positives}
                        negatives={review.negatives}
                    />
                )}

                {review.photos.length > 0 && (
                    <div className={styles.Review__photos}>
                        <PhotosRow
                            photoVersion="s720x720"
                            maxWidth={MAX_WIDTH}
                            photos={review.photos.map((p) => p.photoFile)}
                            linkTo={galleryLinkTo}
                        />
                        <div className={styles.Review__photos__count}>
                            {interpolate(counter_PHOTOS, {
                                count: review.photos.length,
                                counter: review.photos.length,
                            })}
                        </div>
                    </div>
                )}

                <div className={styles.Review__likes}>
                    <div className={styles.Review__likes__likesCount}>
                        {review.likeable?.likesCount > 0 &&
                            interpolate(count_PEOPLE_THINK_IT_IS_USEFUL, {
                                count: review.likeable.likesCount,
                            })}
                    </div>
                    <Like
                        id={review.id}
                        entity="strollers.strolleruserreview"
                        type={LikeType.IconLabel}
                        label={USEFUL}
                    />
                </div>
            </div>
        );
    },
);

Review.displayName = "Review";
